import { useState, useRef, useCallback, useEffect } from 'react'
import { ConnectionStatus, ConfigUiHints, ConfigSnapshot, ConfigSchemaResponse } from '../types'
import { GatewayRpcClient } from '../services/gateway-rpc'
import { getConfig, getSchema, patchConfig as patchConfigRpc, isBaseHashConflictError } from '../services/config-api'
import { loadEquabotSettings, saveEquabotSettings } from '../services/storage'

export type UseGatewayReturn = {
  connectionStatus: ConnectionStatus
  errorMessage: string | null
  config: Record<string, unknown> | null
  schema: Record<string, unknown> | null
  uiHints: ConfigUiHints | null
  baseHash: string | null
  gatewayUrl: string
  setGatewayUrl: (url: string) => void
  token: string
  setToken: (token: string) => void
  password: string
  setPassword: (password: string) => void
  connect: () => void
  disconnect: () => void
  patchConfig: (patch: Record<string, unknown>) => Promise<boolean>
  refreshConfig: () => Promise<void>
}

/**
 * React hook managing Gateway WebSocket connection lifecycle, config, and schema state.
 * Per C7: loads full config + schema on connect. Per C2: stores connection settings in localStorage.
 */
export function useGateway(): UseGatewayReturn {
  const saved = loadEquabotSettings()

  const [gatewayUrl, setGatewayUrlState] = useState(saved.gatewayUrl)
  const [token, setTokenState] = useState(saved.token)
  const [password, setPasswordState] = useState(saved.password)
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [config, setConfig] = useState<Record<string, unknown> | null>(null)
  const [schema, setSchema] = useState<Record<string, unknown> | null>(null)
  const [uiHints, setUiHints] = useState<ConfigUiHints | null>(null)
  const [baseHash, setBaseHash] = useState<string | null>(null)

  const clientRef = useRef<GatewayRpcClient | null>(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (clientRef.current) {
        clientRef.current.disconnect()
        clientRef.current = null
      }
    }
  }, [])

  // --- persistence (nested setState avoids stale closures in useCallback deps) ---

  const persistSettings = useCallback((url: string, tok: string, pw: string) => {
    saveEquabotSettings({ gatewayUrl: url, token: tok, password: pw })
  }, [])

  const setGatewayUrl = useCallback((url: string) => {
    setGatewayUrlState(url)
    setTokenState((tok) => {
      setPasswordState((pw) => { persistSettings(url, tok, pw); return pw })
      return tok
    })
  }, [persistSettings])

  const setToken = useCallback((tok: string) => {
    setTokenState(tok)
    setGatewayUrlState((url) => {
      setPasswordState((pw) => { persistSettings(url, tok, pw); return pw })
      return url
    })
  }, [persistSettings])

  const setPassword = useCallback((pw: string) => {
    setPasswordState(pw)
    setGatewayUrlState((url) => {
      setTokenState((tok) => { persistSettings(url, tok, pw); return tok })
      return url
    })
  }, [persistSettings])

  // --- config/schema loading (C7: load everything on connect) ---

  const loadConfigAndSchema = useCallback(async (client: GatewayRpcClient) => {
    if (!client.connected) return

    try {
      const [configRes, schemaRes] = await Promise.all([
        getConfig(client),
        getSchema(client),
      ]) as [ConfigSnapshot, ConfigSchemaResponse]

      if (!mountedRef.current) return

      setConfig(configRes.config ?? null)
      setBaseHash(configRes.hash ?? null)
      setSchema((schemaRes.schema as Record<string, unknown>) ?? null)
      setUiHints(schemaRes.uiHints ?? null)
      setErrorMessage(null)
    } catch (err) {
      if (!mountedRef.current) return
      setErrorMessage(`Failed to load config: ${err instanceof Error ? err.message : String(err)}`)
    }
  }, [])

  // --- connect / disconnect ---

  const connect = useCallback(() => {
    if (clientRef.current) {
      clientRef.current.disconnect()
      clientRef.current = null
    }

    setErrorMessage(null)
    setConfig(null)
    setSchema(null)
    setUiHints(null)
    setBaseHash(null)

    const client = new GatewayRpcClient()
    clientRef.current = client

    client.onConnectionChange((status, errMsg) => {
      if (!mountedRef.current) return
      setConnectionStatus(status)
      if (status === 'connected') {
        setErrorMessage(null)
        void loadConfigAndSchema(client)
      } else if (status === 'error' && errMsg) {
        setErrorMessage(errMsg)
      }
    })

    persistSettings(gatewayUrl, token, password)
    client.connect(gatewayUrl, token || undefined, password || undefined)
  }, [gatewayUrl, token, password, loadConfigAndSchema, persistSettings])

  const disconnect = useCallback(() => {
    if (clientRef.current) {
      clientRef.current.disconnect()
      clientRef.current = null
    }
    setConnectionStatus('disconnected')
    setErrorMessage(null)
    setConfig(null)
    setSchema(null)
    setUiHints(null)
    setBaseHash(null)
  }, [])

  // --- config mutation ---

  const patchConfig = useCallback(async (
    patch: Record<string, unknown>
  ): Promise<boolean> => {
    const client = clientRef.current
    if (!client?.connected || !baseHash) return false

    try {
      await patchConfigRpc(client, patch, baseHash)
      if (!mountedRef.current) return false
      await loadConfigAndSchema(client)
      return true
    } catch (err) {
      if (!mountedRef.current) return false
      if (isBaseHashConflictError(err)) {
        setErrorMessage('config_conflict')
      } else {
        setErrorMessage(err instanceof Error ? err.message : 'Patch failed')
      }
      return false
    }
  }, [baseHash, loadConfigAndSchema])

  const refreshConfig = useCallback(async () => {
    const client = clientRef.current
    if (!client?.connected) return
    setErrorMessage(null)
    await loadConfigAndSchema(client)
  }, [loadConfigAndSchema])

  return {
    connectionStatus,
    errorMessage,
    config,
    schema,
    uiHints,
    baseHash,
    gatewayUrl,
    setGatewayUrl,
    token,
    setToken,
    password,
    setPassword,
    connect,
    disconnect,
    patchConfig,
    refreshConfig,
  }
}
